import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Wifi, Check, X, RefreshCw, Upload, Download, Link2, ArrowRightLeft,
  Building, Bed, Globe, Loader2, AlertCircle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { propertyAPI, roomAPI } from '@/services/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function ChannexPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [activeTab, setActiveTab] = useState('connection');

  // Connection state
  const [apiKey, setApiKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [channexProperties, setChannexProperties] = useState([]);
  const [selectedChannexProp, setSelectedChannexProp] = useState('');
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  // Mapping state
  const [edenRooms, setEdenRooms] = useState([]);
  const [channexRoomTypes, setChannexRoomTypes] = useState([]);
  const [roomMappings, setRoomMappings] = useState([]);
  const [savingMapping, setSavingMapping] = useState(false);

  // Availability state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Bookings state
  const [channexBookings, setChannexBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importBooking, setImportBooking] = useState(null);
  const [importRoomId, setImportRoomId] = useState('');
  const [importing, setImporting] = useState(false);

  // Rate plans
  const [channexRatePlans, setChannexRatePlans] = useState([]);

  // Inventory state
  const [inventory, setInventory] = useState(null);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [invDateFrom, setInvDateFrom] = useState(() => new Date().toISOString().split('T')[0]);
  const [invDateTo, setInvDateTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().split('T')[0];
  });

  // Rate push state
  const [ratePlanId, setRatePlanId] = useState('');
  const [pushRateFrom, setPushRateFrom] = useState('');
  const [pushRateTo, setPushRateTo] = useState('');
  const [pushRateValue, setPushRateValue] = useState('');
  const [pushingRate, setPushingRate] = useState(false);
  const [creatingRP, setCreatingRP] = useState(false);
  const [newRPTitle, setNewRPTitle] = useState('Tarifa Madre');
  const [newRPOccupancy, setNewRPOccupancy] = useState(2);
  const [newRPRoomType, setNewRPRoomType] = useState('');

  useEffect(() => {
    propertyAPI.getAll().then(res => {
      setProperties(res.data);
      
    });
  }, []);

  const fetchConfig = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/channex/config/${selectedProperty}`, authHeaders());
      setConfig(res.data);
      if (res.data.connected) {
        setApiKey(res.data.channex_api_key || '');
        setSelectedChannexProp(res.data.channex_property_id || '');
      }
    } catch { setConfig(null); }
  }, [selectedProperty]);

  const fetchEdenRooms = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await roomAPI.getAll(selectedProperty);
      setEdenRooms(res.data);
    } catch {}
  }, [selectedProperty]);

  const fetchChannexRoomTypes = useCallback(async () => {
    if (!selectedProperty || !config?.connected) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/channex/room-types/${selectedProperty}`, authHeaders());
      setChannexRoomTypes(res.data);
    } catch {}
  }, [selectedProperty, config]);

  const fetchRoomMapping = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/channex/room-mapping/${selectedProperty}`, authHeaders());
      setRoomMappings(res.data.mappings || []);
    } catch {}
  }, [selectedProperty]);

  useEffect(() => { fetchConfig(); fetchEdenRooms(); }, [fetchConfig, fetchEdenRooms]);
  useEffect(() => { if (config?.connected) { fetchChannexRoomTypes(); fetchRoomMapping(); } }, [config, fetchChannexRoomTypes, fetchRoomMapping]);
  useEffect(() => { if (config?.connected && activeTab === 'inventory') { fetchInventory(); } }, [activeTab, config, selectedProperty]);

  // Test connection
  const testConnection = async () => {
    if (!apiKey.trim()) { toast.error('Ingresa la API Key'); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/channex/test-connection`, { api_key: apiKey }, authHeaders());
      setChannexProperties(res.data.properties);
      toast.success(`Conexión exitosa - ${res.data.properties.length} propiedad(es) encontrada(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error de conexión');
      setChannexProperties([]);
    } finally { setTesting(false); }
  };

  // Save config
  const saveConfig = async () => {
    if (!selectedChannexProp) { toast.error('Selecciona una propiedad de Channex'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/channex/config`, {
        property_id: selectedProperty,
        channex_api_key: apiKey,
        channex_property_id: selectedChannexProp
      }, authHeaders());
      toast.success('Configuración guardada');
      fetchConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // Save room mapping
  const saveRoomMapping = async () => {
    setSavingMapping(true);
    try {
      await axios.post(`${BACKEND_URL}/api/channex/map-rooms`, {
        property_id: selectedProperty,
        mappings: roomMappings
      }, authHeaders());
      toast.success('Mapeo de habitaciones guardado');
    } catch (e) {
      toast.error('Error al guardar mapeo');
    } finally { setSavingMapping(false); }
  };

  const updateMapping = (edenRoomId, channexRoomTypeId) => {
    setRoomMappings(prev => {
      const filtered = prev.filter(m => m.eden_room_id !== edenRoomId);
      if (channexRoomTypeId) {
        filtered.push({ eden_room_id: edenRoomId, channex_room_type_id: channexRoomTypeId });
      }
      return filtered;
    });
  };

  // Push availability
  const pushAvailability = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/channex/push-availability/${selectedProperty}`, {}, authHeaders());
      setSyncResult(res.data);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sincronizando');
    } finally { setSyncing(false); }
  };

  // Pull bookings
  const pullBookings = async () => {
    setLoadingBookings(true);
    try {
      const [bookingsRes, ratePlansRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/channex/bookings/${selectedProperty}`, authHeaders()),
        axios.get(`${BACKEND_URL}/api/channex/rate-plans/${selectedProperty}`, authHeaders()).catch(() => ({ data: [] }))
      ]);
      setChannexBookings(bookingsRes.data);
      setChannexRatePlans(ratePlansRes.data);
      if (bookingsRes.data.length === 0) toast.info('No hay reservas en Channex');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error obteniendo reservas');
    } finally { setLoadingBookings(false); }
  };

  // Import booking
  const handleImportBooking = async () => {
    if (!importRoomId) { toast.error('Selecciona una habitación'); return; }
    setImporting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/channex/import-booking`, {
        property_id: selectedProperty,
        booking: importBooking,
        eden_room_id: importRoomId
      }, authHeaders());
      toast.success('Reserva importada exitosamente');
      setImportModal(false);
      setImportBooking(null);
      pullBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error importando reserva');
    } finally { setImporting(false); }
  };

  // Fetch inventory
  const fetchInventory = async () => {
    setLoadingInventory(true);
    try {
      const [invRes, rpRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/channex/inventory/${selectedProperty}?date_from=${invDateFrom}&date_to=${invDateTo}`, authHeaders()),
        axios.get(`${BACKEND_URL}/api/channex/rate-plans/${selectedProperty}`, authHeaders()).catch(() => ({ data: [] }))
      ]);
      setInventory(invRes.data);
      setChannexRatePlans(rpRes.data);
      if (rpRes.data.length > 0 && !ratePlanId) setRatePlanId(rpRes.data[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando inventario');
    } finally { setLoadingInventory(false); }
  };

  // Create rate plan
  const createRatePlan = async () => {
    if (!newRPRoomType) { toast.error('Selecciona un Room Type'); return; }
    setCreatingRP(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/channex/create-rate-plan/${selectedProperty}`, {
        room_type_id: newRPRoomType,
        title: newRPTitle,
        occupancy: newRPOccupancy,
        default_rate: 0
      }, authHeaders());
      toast.success(`Rate Plan "${res.data.rate_plan.title}" creado`);
      fetchInventory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando Rate Plan');
    } finally { setCreatingRP(false); }
  };

  // Push rates
  const pushRates = async () => {
    if (!ratePlanId || !pushRateFrom || !pushRateTo || !pushRateValue) {
      toast.error('Completa todos los campos'); return;
    }
    setPushingRate(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/channex/push-rates/${selectedProperty}`, {
        rate_plan_id: ratePlanId,
        date_from: pushRateFrom,
        date_to: pushRateTo,
        rate: parseFloat(pushRateValue)
      }, authHeaders());
      toast.success(res.data.message);
      fetchInventory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error enviando tarifa');
    } finally { setPushingRate(false); }
  };

  const tabs = [
    { id: 'connection', label: 'Conexión', icon: Link2 },
    { id: 'mapping', label: 'Mapeo', icon: ArrowRightLeft },
    { id: 'inventory', label: 'Inventario', icon: Globe },
    { id: 'availability', label: 'Disponibilidad', icon: Upload },
    { id: 'bookings', label: 'Reservas', icon: Download },
  ];

  const connectedPropName = channexProperties.find(p => p.id === selectedChannexProp)?.title
    || (config?.connected ? 'Conectada' : '');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="channex-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Channel Manager</h1>
            <p className="text-muted-foreground mt-1">Integración con Channex para sincronizar OTAs</p>
          </div>
          <div className="flex items-center gap-3">
            {config?.connected && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 gap-1">
                <CheckCircle2 className="h-3 w-3" /> Channex Conectado
              </Badge>
            )}
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[250px]" data-testid="channex-property-selector">
                  <SelectValue placeholder="Propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all
                ${activeTab === tab.id ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* CONNECTION TAB */}
        {activeTab === 'connection' && (
          <div className="space-y-4">
            <Card data-testid="channex-connection-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="h-5 w-5" /> Conectar con Channex (Sandbox)
                </CardTitle>
                <CardDescription>Ingresa tu API Key de staging.channex.io para conectar</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm">API Key de Channex</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="password"
                      placeholder="Tu API Key de Channex..."
                      value={apiKey}
                      onChange={e => setApiKey(e.target.value)}
                      data-testid="channex-api-key-input"
                      className="flex-1"
                    />
                    <Button onClick={testConnection} disabled={testing} data-testid="btn-test-connection">
                      {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wifi className="h-4 w-4 mr-1" />}
                      Probar
                    </Button>
                  </div>
                </div>

                {channexProperties.length > 0 && (
                  <div className="space-y-3">
                    <Separator />
                    <Label className="text-sm font-medium">Selecciona la propiedad de Channex a vincular:</Label>
                    <div className="grid gap-2">
                      {channexProperties.map(cp => (
                        <button
                          key={cp.id}
                          onClick={() => setSelectedChannexProp(cp.id)}
                          className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all text-left
                            ${selectedChannexProp === cp.id
                              ? 'border-primary bg-primary/5'
                              : 'border-muted hover:border-primary/30'}`}
                        >
                          <div className="flex items-center gap-3">
                            <Building className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{cp.title}</p>
                              <p className="text-xs text-muted-foreground">{cp.city}, {cp.country} - {cp.currency}</p>
                            </div>
                          </div>
                          {selectedChannexProp === cp.id && <Check className="h-5 w-5 text-primary" />}
                        </button>
                      ))}
                    </div>
                    <Button onClick={saveConfig} disabled={saving || !selectedChannexProp} className="w-full" data-testid="btn-save-channex-config">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                      Guardar Conexión
                    </Button>
                  </div>
                )}

                {config?.connected && channexProperties.length === 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Conectado a Channex</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">Propiedad vinculada: {config.channex_property_id}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* MAPPING TAB */}
        {activeTab === 'mapping' && (
          <div className="space-y-4">
            {!config?.connected ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Primero conecta tu propiedad a Channex en la pestaña "Conexión"</p>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="room-mapping-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5" /> Mapeo de Habitaciones
                  </CardTitle>
                  <CardDescription>Vincula cada habitación de Edén con un tipo de habitación en Channex</CardDescription>
                </CardHeader>
                <CardContent>
                  {edenRooms.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No hay habitaciones en Edén</p>
                  ) : channexRoomTypes.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No hay Room Types en Channex. Crea uno en staging.channex.io</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-center text-xs font-semibold text-muted-foreground pb-2 border-b">
                        <span>Habitación Edén</span>
                        <span></span>
                        <span>Room Type Channex</span>
                      </div>
                      {edenRooms.map(room => {
                        const currentMapping = roomMappings.find(m => m.eden_room_id === room.id);
                        return (
                          <div key={room.id} className="grid grid-cols-[1fr,auto,1fr] gap-3 items-center">
                            <div className="flex items-center gap-2 bg-muted/50 p-2 rounded-lg">
                              <Bed className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">{room.nombre}</p>
                                <p className="text-xs text-muted-foreground">{room.tipo}</p>
                              </div>
                            </div>
                            <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                            <Select
                              value={currentMapping?.channex_room_type_id || ''}
                              onValueChange={val => updateMapping(room.id, val)}
                            >
                              <SelectTrigger className="w-full" data-testid={`mapping-${room.id}`}>
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {channexRoomTypes.map(crt => (
                                  <SelectItem key={crt.id} value={crt.id}>
                                    {crt.title} ({crt.count_of_rooms} hab.)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                      <Button onClick={saveRoomMapping} disabled={savingMapping} className="w-full mt-3" data-testid="btn-save-mapping">
                        {savingMapping ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                        Guardar Mapeo
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}


        {/* INVENTORY TAB */}
        {activeTab === 'inventory' && (
          <div className="space-y-4">
            {!config?.connected ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Primero conecta tu propiedad a Channex</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Rate Plans + Push Rates */}
                <Card data-testid="rate-plan-card">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="h-5 w-5" /> Tarifa Madre y Rate Plans
                    </CardTitle>
                    <CardDescription>Gestiona tus Rate Plans y envía tarifas a Channex</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Existing rate plans */}
                    {channexRatePlans.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-muted-foreground">Rate Plans existentes en Channex:</Label>
                        <div className="flex flex-wrap gap-2">
                          {channexRatePlans.map(rp => (
                            <Badge
                              key={rp.id}
                              variant={ratePlanId === rp.id ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => setRatePlanId(rp.id)}
                            >
                              {rp.title} ({rp.currency}) - {rp.sell_mode}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Create new rate plan */}
                    {channexRatePlans.length === 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm text-amber-700 font-medium">No hay Rate Plans. Crea tu Tarifa Madre:</p>
                        <div className="grid sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input value={newRPTitle} onChange={e => setNewRPTitle(e.target.value)} data-testid="input-rp-title" />
                          </div>
                          <div>
                            <Label className="text-xs">Ocupación máx.</Label>
                            <Input type="number" value={newRPOccupancy} onChange={e => setNewRPOccupancy(parseInt(e.target.value) || 1)} data-testid="input-rp-occ" />
                          </div>
                          <div>
                            <Label className="text-xs">Room Type</Label>
                            <Select value={newRPRoomType} onValueChange={setNewRPRoomType}>
                              <SelectTrigger data-testid="select-rp-room-type"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                              <SelectContent>
                                {channexRoomTypes.map(rt => (
                                  <SelectItem key={rt.id} value={rt.id}>{rt.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button onClick={createRatePlan} disabled={creatingRP} data-testid="btn-create-rate-plan">
                          {creatingRP ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                          Crear Rate Plan
                        </Button>
                      </div>
                    )}

                    {/* Push rates */}
                    {channexRatePlans.length > 0 && (
                      <>
                        <Separator />
                        <Label className="text-sm font-semibold">Enviar Tarifa Madre a Channex</Label>
                        <div className="grid sm:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Desde</Label>
                            <Input type="date" value={pushRateFrom} onChange={e => setPushRateFrom(e.target.value)} data-testid="input-rate-from" />
                          </div>
                          <div>
                            <Label className="text-xs">Hasta</Label>
                            <Input type="date" value={pushRateTo} onChange={e => setPushRateTo(e.target.value)} data-testid="input-rate-to" />
                          </div>
                          <div>
                            <Label className="text-xs">Tarifa (COP)</Label>
                            <Input type="number" placeholder="150000" value={pushRateValue} onChange={e => setPushRateValue(e.target.value)} data-testid="input-rate-value" />
                          </div>
                          <div className="flex items-end">
                            <Button onClick={pushRates} disabled={pushingRate} className="w-full" data-testid="btn-push-rates">
                              {pushingRate ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                              Enviar Tarifa
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Inventory Calendar View */}
                <Card data-testid="inventory-calendar-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Inventario Channex</CardTitle>
                        <CardDescription>Disponibilidad y tarifas actuales en Channex</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="date" value={invDateFrom} onChange={e => setInvDateFrom(e.target.value)} className="w-40 h-8 text-xs" />
                        <span className="text-xs text-muted-foreground">a</span>
                        <Input type="date" value={invDateTo} onChange={e => setInvDateTo(e.target.value)} className="w-40 h-8 text-xs" />
                        <Button size="sm" onClick={fetchInventory} disabled={loadingInventory} data-testid="btn-fetch-inventory">
                          {loadingInventory ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {!inventory ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Globe className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>Haz clic en el botón de actualizar para cargar el inventario</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        {(() => {
                          // Build dates array
                          const dates = [];
                          const start = new Date(invDateFrom + 'T12:00:00');
                          const end = new Date(invDateTo + 'T12:00:00');
                          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                            dates.push(d.toISOString().split('T')[0]);
                          }
                          const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

                          // Room type availability
                          const avlData = inventory.availability || {};
                          const restData = inventory.restrictions || {};

                          return (
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="text-left py-2 px-2 sticky left-0 bg-muted/50 min-w-[120px] z-10">Tipo</th>
                                  <th className="text-center py-2 px-2 min-w-[40px]"></th>
                                  {dates.map(date => {
                                    const dt = new Date(date + 'T12:00:00');
                                    return (
                                      <th key={date} className="text-center py-1 px-1 min-w-[55px]">
                                        <div className="text-[10px] text-muted-foreground">{dayNames[dt.getDay()]}</div>
                                        <div className="font-bold">{dt.getDate()}</div>
                                        <div className="text-[10px] text-muted-foreground">{['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][dt.getMonth()]}</div>
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {/* Availability per room type */}
                                {Object.entries(avlData).map(([rtId, dateAvl]) => {
                                  const rt = channexRoomTypes.find(r => r.id === rtId);
                                  return (
                                    <tr key={`avl-${rtId}`} className="border-b">
                                      <td className="py-2 px-2 sticky left-0 bg-white z-10 font-semibold text-blue-700">{rt?.title || rtId.slice(0,8)}</td>
                                      <td className="py-2 px-2 text-center font-medium text-muted-foreground">AVL</td>
                                      {dates.map(date => {
                                        const avl = dateAvl[date];
                                        return (
                                          <td key={date} className={`py-2 px-1 text-center font-bold ${avl === 0 ? 'bg-red-50 text-red-600' : avl <= 2 ? 'bg-yellow-50 text-yellow-700' : 'text-green-700'}`}>
                                            {avl !== undefined ? avl : '-'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                                {/* Rates per rate plan */}
                                {Object.entries(restData).map(([rpId, dateRest]) => {
                                  const rp = channexRatePlans.find(r => r.id === rpId);
                                  return (
                                    <tr key={`rate-${rpId}`} className="border-b bg-blue-50/30">
                                      <td className="py-2 px-2 sticky left-0 bg-blue-50/30 z-10 font-medium text-purple-700">{rp?.title || rpId.slice(0,8)}</td>
                                      <td className="py-2 px-2 text-center font-medium text-muted-foreground">$</td>
                                      {dates.map(date => {
                                        const rest = dateRest[date];
                                        const rate = rest?.rate;
                                        const rateDisplay = rate ? `$${(parseFloat(rate) * 100).toLocaleString('es-CO', {maximumFractionDigits: 0})}` : '-';
                                        return (
                                          <td key={date} className={`py-2 px-1 text-center text-xs font-medium ${rate && parseFloat(rate) > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                                            {rateDisplay}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* AVAILABILITY TAB */}
        {activeTab === 'availability' && (
          <div className="space-y-4">
            {!config?.connected ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Primero conecta tu propiedad a Channex</p>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="availability-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Upload className="h-5 w-5" /> Sincronizar Disponibilidad
                  </CardTitle>
                  <CardDescription>Envía la disponibilidad actual de Edén a Channex (próximos 90 días)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                    <p>Al sincronizar, se calculará la disponibilidad de cada habitación mapeada para los próximos 90 días y se enviará a Channex. Las OTAs conectadas recibirán la actualización automáticamente.</p>
                  </div>
                  <Button
                    onClick={pushAvailability}
                    disabled={syncing}
                    size="lg"
                    className="w-full"
                    data-testid="btn-push-availability"
                  >
                    {syncing ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sincronizando...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> Enviar Disponibilidad a Channex</>
                    )}
                  </Button>
                  {syncResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-medium">{syncResult.message}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* BOOKINGS TAB */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {!config?.connected ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Primero conecta tu propiedad a Channex</p>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="bookings-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Download className="h-5 w-5" /> Reservas de Channex
                      </CardTitle>
                      <CardDescription>Reservas recibidas desde OTAs vía Channex</CardDescription>
                    </div>
                    <Button onClick={pullBookings} disabled={loadingBookings} variant="outline" data-testid="btn-pull-bookings">
                      {loadingBookings ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                      Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {channexBookings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Download className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No hay reservas en Channex</p>
                      <p className="text-xs mt-1">Las reservas de OTAs conectadas aparecerán aquí</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left py-2 px-3">Huésped</th>
                            <th className="text-left py-2 px-3">Check-in</th>
                            <th className="text-left py-2 px-3">Check-out</th>
                            <th className="text-left py-2 px-3">Canal</th>
                            <th className="text-right py-2 px-3">Monto</th>
                            <th className="text-center py-2 px-3">Estado</th>
                            <th className="text-center py-2 px-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {channexBookings.map((b, i) => (
                            <tr key={i} className="border-b hover:bg-muted/30">
                              <td className="py-2 px-3 font-medium">{b.guest_name || 'Sin nombre'}</td>
                              <td className="py-2 px-3 text-xs">{b.arrival_date}</td>
                              <td className="py-2 px-3 text-xs">{b.departure_date}</td>
                              <td className="py-2 px-3">
                                <Badge variant="outline">{b.source}</Badge>
                              </td>
                              <td className="py-2 px-3 text-right font-medium">
                                {b.currency} {parseFloat(b.total_amount || 0).toLocaleString('es-CO')}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <Badge variant={b.status === 'new' ? 'default' : 'secondary'}>{b.status}</Badge>
                              </td>
                              <td className="py-2 px-3 text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setImportBooking(b); setImportRoomId(''); setImportModal(true); }}
                                  data-testid={`btn-import-${i}`}
                                >
                                  Importar
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Import Booking Modal */}
        <Dialog open={importModal} onOpenChange={setImportModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Importar Reserva de Channex</DialogTitle>
            </DialogHeader>
            {importBooking && (
              <div className="space-y-4">
                <div className="bg-muted/50 p-3 rounded-lg space-y-1 text-sm">
                  <p><strong>Huésped:</strong> {importBooking.guest_name}</p>
                  <p><strong>Check-in:</strong> {importBooking.arrival_date}</p>
                  <p><strong>Check-out:</strong> {importBooking.departure_date}</p>
                  <p><strong>Canal:</strong> {importBooking.source}</p>
                  <p><strong>Monto:</strong> {importBooking.currency} {parseFloat(importBooking.total_amount || 0).toLocaleString('es-CO')}</p>
                </div>
                <div>
                  <Label>Asignar a habitación</Label>
                  <Select value={importRoomId} onValueChange={setImportRoomId}>
                    <SelectTrigger data-testid="import-room-select">
                      <SelectValue placeholder="Selecciona habitación..." />
                    </SelectTrigger>
                    <SelectContent>
                      {edenRooms.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nombre} - {r.tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportModal(false)}>Cancelar</Button>
              <Button onClick={handleImportBooking} disabled={importing} data-testid="btn-confirm-import">
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                Importar Reserva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
